<template lang="pug">
  .app-card.overflow-x-auto
    // Header Section with Better Styling
    .container.mb-4
      .row.align-items-center.py-3.border-bottom
        .col-auto
          
        .col
        
        .col-auto(v-if="isDisabled")
          .badge.bg-secondary.px-3.py-2
            i.fa.fa-lock.me-1
            | Order {{ data_order.state.name }}

    .container
      .row.g-4
        // Left Column - Service Selection
        .col-lg-6
          .card.h-100.shadow-sm
            .card-header.bg-light
              .d-flex.align-items-center
                i.fa.fa-list-check.me-2.text-primary
                h5.mb-0.fw-bold Marmic Fire and Safety Services
                .ms-auto.d-flex.align-items-center.gap-2
                  .badge.bg-info {{ selectedServices.length }} selected
                  button.btn.btn-outline-secondary.btn-sm(
                    v-if="hasSelectedServices && !isDisabled"
                    @click="clearAllServices"
                    :disabled="state.sending"
                  )
                    i.fa.fa-times.me-1
                    | Clear All
            .card-body
              // Standard Services Grid
              .row.g-2
                .col-md-6(v-for="service in visibleServices" :key="service.id")
                  .service-card(:class="{ 'selected': service.selected, 'disabled': isDisabled }")
                    .form-check.h-100.p-3
                      input.form-check-input.me-2(
                        type="checkbox"
                        :id="'service-'+service.id" 
                        v-model="service.selected"
                        @change="onServiceSelect(service)"
                        :disabled="isDisabled"
                      )
                      label.form-check-label.d-flex.flex-column.flex-grow-1(:for="'service-'+service.id")
                        .d-flex.align-items-center.mb-2
                          i(:class="['fa', iconMapping[service.name] || 'fa-cube', 'me-2', service.selected ? 'text-primary' : 'text-muted']")
                          span.fw-semibold.small {{ service.name }}
                        .mt-auto(v-if="service.selected")
                          .badge.bg-success.small
                            i.fa.fa-check.me-1
                            | Selected

              // Custom Service Section
              .mt-4
                .border-top.pt-4
                  .d-flex.align-items-center.mb-3
                    i.fa.fa-plus-circle.me-2.text-success
                    h6.mb-0.fw-bold Custom Service
                  .custom-service-card(:class="{ 'selected': customService.selected, 'disabled': isDisabled }")
                    .p-3
                      .form-check.mb-3
                        input.form-check-input(
                          type="checkbox" 
                          id="custom-service" 
                          v-model="customService.selected"
                          @change="onCustomServiceSelect"
                          :disabled="isDisabled"
                        )
                        label.form-check-label.fw-semibold(for="custom-service") 
                          i.fa.fa-wrench.me-2
                          | Add Custom Service
                      
                      // Custom Service Fields
                      .custom-fields(v-if="customService.selected")
                        .row.g-2
                          .col-8
                            .form-floating
                              input.form-control(
                                :class="{ 'is-invalid': customService.hasNameError }"
                                type="text"
                                id="customServiceName"
                                :placeholder="$I18n.t('vue.marmic_services.custom_service_name_placeholder', {default: 'Service name'})"
                                v-model="customService.name"
                                @input="clearCustomServiceNameError"
                                :disabled="isDisabled"
                              )
                              label(for="customServiceName") Service Description
                            .invalid-feedback.small(v-if="customService.hasNameError")
                              i.fa.fa-exclamation-triangle.me-1
                              | Service name is required
                          .col-4
                            .form-floating
                              input.form-control(
                                :class="{ 'is-invalid': customService.hasPriceError }"
                                type="number"
                                id="customServicePrice"
                                step="0.01"
                                min="0"
                                :placeholder="$I18n.t('vue.marmic_services.custom_service_price_placeholder', {default: '0.00'})"
                                v-model="customService.price"
                                @input="clearCustomServicePriceError"
                                :disabled="isDisabled"
                              )
                              label(for="customServicePrice") Price ($)
                            .invalid-feedback.small(v-if="customService.hasPriceError")
                              i.fa.fa-exclamation-triangle.me-1
                              | Valid price greater than $0 is required

        // Right Column - Price Configuration
        .col-lg-6(v-if="hasSelectedServices")
          .card.h-100.shadow-sm(:class="{ 'opacity-50': isDisabled }")
            .card-header.bg-primary.text-white
              .d-flex.align-items-center
                i.fa.fa-dollar-sign.me-2
                h5.mb-0.fw-bold Price Configuration
                .ms-auto
                  .badge.bg-light.text-primary {{ formatCurrency(totalPriceWithCustom) }}
            .card-body
              // Selected Services List
              .selected-services-list
                .service-price-item.p-3.border.rounded.mb-2(
                  v-for="service in selectedServices" 
                  :key="service.id"
                  :class="{ 'has-error': service.hasError }"
                )
                  .d-flex.align-items-center.mb-2
                    i(:class="['fa', iconMapping[service.name] || 'fa-cube', 'me-2', 'text-primary']")
                    span.fw-semibold {{ service.name }}
                  
                  // Price Selection for different service types
                  .price-section
                    div(v-if="service.name === 'Compliance Engine Submission Fee'")
                      label.form-label.small.text-muted Custom Price
                      .input-group
                        span.input-group-text $
                        input.form-control(
                          :class="{ 'is-invalid': service.hasError }"
                          type="number"
                          step="0.01"
                          min="0"
                          :placeholder="$I18n.t('vue.marmic_services.compliance_fee_placeholder', {default: 'Enter amount'})"
                          v-model="service.selectedPrice"
                          @input="clearServiceError(service)"
                          :disabled="isDisabled"
                        )
                      .invalid-feedback.small(v-if="service.hasError")
                        i.fa.fa-exclamation-triangle.me-1
                        | This service requires a valid price greater than $0
                    div(v-else)
                      label.form-label.small.text-muted Select Price
                      select.form-select(
                        :class="{ 'is-invalid': service.hasError }"
                        v-model="service.selectedPrice"
                        @change="clearServiceError(service)"
                        :disabled="isDisabled"
                      )
                        option(value="" disabled) Choose a price option
                        option(v-for="price in prices" 
                          :key="price.value" 
                          :value="price.value") {{ formatCurrency(price.value) }}
                      .invalid-feedback.small(v-if="service.hasError")
                        i.fa.fa-exclamation-triangle.me-1
                        | Please select a price for this service
                
                // Custom Service Display
                .service-price-item.p-3.border.rounded.mb-2.border-success(
                  v-if="customService.selected && customService.name && customService.price"
                )
                  .d-flex.align-items-center.justify-content-between
                    .d-flex.align-items-center
                      i.fa.fa-plus-circle.me-2.text-success
                      span.fw-semibold {{ customService.name }}
                    .fw-bold.text-success {{ formatCurrency(customService.price) }}

    // Summary and Actions Section  
    .container.mt-4(v-if="servicesWithPricesAndCustom.length > 0")
      .card.shadow
        .card-body
          // Summary Table
          .table-responsive
            table.table.table-hover.mb-3
              thead.table-dark
                tr
                  th.fw-bold.text-dark Service Description
                  th.text-end.fw-bold.text-dark Price
              tbody
                tr(v-for="service in servicesWithPricesAndCustom" :key="service.id || 'custom'")
                  td
                    .d-flex.align-items-center
                      i(:class="['fa', iconMapping[service.name] || 'fa-cube', 'me-2']")
                      | {{ service.name }}
                  td.text-end.fw-semibold {{ formatCurrency(service.selectedPrice || service.price) }}
              tfoot.table-dark
                tr
                  td.text-end.fw-bold.fs-5 TOTAL
                  td.text-end.fw-bold.fs-5.text-success {{ formatCurrency(totalPriceWithCustom) }}
          
          // Options Section
          .row.align-items-center.mt-3
            .col-md-8
              .form-check.form-switch
                input.form-check-input(
                  type="checkbox" 
                  id="save-for-next-orders"
                  v-model="saveForNextOrders"
                  :disabled="isDisabled"
                )
                label.form-check-label.fw-semibold(for="save-for-next-orders")
                  i.fa.fa-bookmark.me-2
                  | {{ $I18n.t('vue.marmic_services.save_for_next_orders', {default: 'Save this configuration for next orders'}) }}
            .col-md-4.text-end(v-if="hasChanges && !isDisabled")
              div(v-if="state.sending")
                .spinner-border.spinner-border-sm.me-2(role="status")
                span.text-muted Saving...
              div(v-else)  
                button.btn.btn-success.btn-lg.px-4(@click="save_services" :disabled="isDisabled")
                  i.fa.fa-save.me-2
                  | {{$I18n.t('vue.units_detail_table_panel.save_units', {default: 'Save Services'})}}

    // Save Actions when no services selected but there are changes
    .container.mt-4(v-if="hasChanges && servicesWithPricesAndCustom.length === 0 && !isDisabled")
      .card.shadow
        .card-body
          .row.align-items-center
            .col-md-8
              .d-flex.align-items-center
                i.fa.fa-info-circle.me-2.text-info
                span.text-muted All services have been cleared. Save to confirm changes.
            .col-md-4.text-end
              div(v-if="state.sending")
                .spinner-border.spinner-border-sm.me-2(role="status")
                span.text-muted Saving...
              div(v-else)  
                button.btn.btn-success.btn-lg.px-4(@click="save_services")
                  i.fa.fa-save.me-2
                  | Save Changes
    
    // Error Alert
    .alert.alert-danger.mt-3.d-flex.justify-content-between.align-items-center(v-if="errorMessage")
      .d-flex.align-items-center
        i.fa.fa-exclamation-triangle.me-2
        span {{ errorMessage }}
      button.btn-close(@click="errorMessage = null")
    
    // Success Alert
    .alert.alert-success.mt-3.d-flex.justify-content-between.align-items-center(v-if="successMessage")
      .d-flex.align-items-center
        i.fa.fa-check-circle.me-2
        span {{ successMessage }}
      button.btn-close(@click="successMessage = null")
  </template>
  
  <script>
  import { BackendService } from '../../services'
  import CoreComponents from "../core"
  
  export default {
    components: {
      "app-icon-link": CoreComponents.AppIconLink,
    },
    props: {
      dataId: String,
      dataModel: String,
      dataOrder: String,
    },
    data() {
      return {
        state: {
          sending: false
        },
        services: [
          { id: 1, name: 'Deli', selected: false, selectedPrice: '', hasError: false },
          { id: 2, name: 'Rotisserie', selected: false, selectedPrice: '', hasError: false },
          { id: 3, name: 'Rotisserie (Small)', selected: false, selectedPrice: '', hasError: false },
          { id: 4, name: 'Deli & Rotisserie Combo', selected: false, selectedPrice: '', hasError: false },
          { id: 5, name: 'Bakery', selected: false, selectedPrice: '', hasError: false },
          { id: 6, name: 'Bakery (Small)', selected: false, selectedPrice: '', hasError: false },
          { id: 7, name: 'Bakery Extra', selected: false, selectedPrice: '', hasError: false },
          { id: 8, name: 'Pizza Oven', selected: false, selectedPrice: '', hasError: false },
          { id: 9, name: 'Grill', selected: false, selectedPrice: '', hasError: false },
          { id: 10, name: 'Fryer', selected: false, selectedPrice: '', hasError: false },
          { id: 11, name: 'Burner', selected: false, selectedPrice: '', hasError: false },
          { id: 12, name: 'Prep Line', selected: false, selectedPrice: '', hasError: false },
          { id: 13, name: 'Hinge Kit', selected: false, selectedPrice: '', hasError: false },
          { id: 14, name: 'Access Panel', selected: false, selectedPrice: '', hasError: false },
          { id: 15, name: 'Combo', selected: false, selectedPrice: '', hasError: false },
          { id: 16, name: 'Inspection', selected: false, selectedPrice: '', hasError: false },
          { id: 17, name: 'Emergency service', selected: false, selectedPrice: '', hasError: false },
          { id: 18, name: 'Compliance Engine Submission Fee', selected: false, selectedPrice: '', hasError: false }
        ],
        customService: {
          selected: false,
          name: '',
          price: '',
          hasNameError: false,
          hasPriceError: false
        },
        saveForNextOrders: false,
        prices: [
          { value: 325 },
          { value: 250 },
          { value: 200 },
          { value: 150 },
          { value: 125 },
          { value: 100 },
          { value: 25 }
        ],
        record: {
          id: this.dataId,
          model: this.dataModel
        },
        originalServices: null,
        data_order: this.dataOrder ? JSON.parse(this.dataOrder) : { id: "new", state: { name: "DRAFT" }, marmic_services: '{"services": []}' },
        errorMessage: null,
        successMessage: null,
        iconMapping: {
          'Deli': 'fa-cutlery',
          'Rotisserie': 'fa-fire',
          'Rotisserie (Small)': 'fa-fire',
          'Deli & Rotisserie Combo': 'fa-th-large',
          'Bakery': 'fa-leaf',
          'Bakery (Small)': 'fa-leaf',
          'Bakery Extra': 'fa-leaf',
          'Pizza Oven': 'fa-circle-o-notch',
          'Grill': 'fa-th',
          'Fryer': 'fa-tint',
          'Burner': 'fa-free-code-camp',
          'Prep Line': 'fa-sliders',
          'Hinge Kit': 'fa-wrench',
          'Access Panel': 'fa-cogs',
          'Combo': 'fa-th-large',
          'Inspection': 'fa-search',
          'Emergency service': 'fa-exclamation-triangle',
          'Compliance Engine Submission Fee': 'fa-file-text-o'
        },
      }
    },
    computed: {
      isDisabled() {
        // No deshabilitar para nuevas órdenes (DRAFT) o si no hay estado definido
        if (!this.data_order || !this.data_order.state || this.data_order.state.name === "DRAFT") {
          return false;
        }
        return this.data_order.state.name === "COMPLETED" || 
               this.data_order.state.name === "CANCELED"
      },
      // Filter services to hide invoice-only services in work orders
      visibleServices() {
        const invoiceOnlyServices = ['Inspection', 'Emergency service', 'Compliance Engine Submission Fee']
        return this.services.filter(service => !invoiceOnlyServices.includes(service.name))
      },
      hasSelectedServices() {
        return this.visibleServices.some(s => s.selected) || this.customService.selected
      },
      selectedServices() {
        return this.visibleServices.filter(s => s.selected)
      },
      servicesWithPrices() {
        return this.visibleServices.filter(s => s.selected && s.selectedPrice)
      },
      servicesWithPricesAndCustom() {
        const regularServices = this.servicesWithPrices
        const customServiceArray = (this.customService.selected && this.customService.name && this.customService.price) 
          ? [this.customService] 
          : []
        return [...regularServices, ...customServiceArray]
      },
      totalPrice() {
        return this.servicesWithPrices.reduce((sum, service) => {
          return sum + parseFloat(service.selectedPrice || 0)
        }, 0)
      },
      totalPriceWithCustom() {
        const regularTotal = this.totalPrice
        const customPrice = (this.customService.selected && this.customService.price) 
          ? parseFloat(this.customService.price) 
          : 0
        return regularTotal + customPrice
      },
      hasChanges() {
        // Puedes habilitar la validación de cambios comparando con el estado original
        return true
      }
    },
    created() {
      // Guardar estado inicial para comparar cambios
      this.originalServices = JSON.stringify(this.services)
      
      console.log('MARMIC DEBUG: Component created for order:', this.data_order?.id)
      console.log('MARMIC DEBUG: Order state:', this.data_order?.state?.name)
      console.log('MARMIC DEBUG: Subsidary ID:', this.data_order?.subsidary?.id)
      console.log('MARMIC DEBUG: Has marmic_services:', !!this.data_order?.marmic_services)
      console.log('MARMIC DEBUG: marmic_services value:', this.data_order?.marmic_services)
      
      // Función mejorada para detectar servicios vacíos incluyendo objetos Observer vacíos
      const isEmptyMarmicServices = (services) => {
        console.log('MARMIC DEBUG: isEmptyMarmicServices called with:', services)
        console.log('MARMIC DEBUG: services type:', typeof services)
        console.log('MARMIC DEBUG: services constructor:', services ? services.constructor : 'null')
        
        // Casos obvios de vacío
        if (!services || services === '' || services === '[]' || services === '{}') {
          console.log('MARMIC DEBUG: Empty - obvious case')
          return true
        }
        
        // Si es un array vacío
        if (Array.isArray(services) && services.length === 0) {
          console.log('MARMIC DEBUG: Empty - empty array')
          return true
        }
        
        // Si es un objeto Observer de Vue, necesitamos verificar de manera diferente
        if (typeof services === 'object' && services.constructor === Object) {
          // Intentar convertir a JSON para extraer los datos reales
          let jsonString = ''
          try {
            jsonString = JSON.stringify(services)
            console.log('MARMIC DEBUG: JSON stringified:', jsonString)
            
            // Si el JSON resultante está vacío o es solo {}, entonces está vacío
            if (jsonString === '{}' || jsonString === '' || jsonString === '[]') {
              console.log('MARMIC DEBUG: Empty - JSON stringify shows empty')
              return true
            }
            
            // Si tiene contenido en el JSON, NO está vacío
            console.log('MARMIC DEBUG: NOT empty - JSON has content')
            return false
            
          } catch (error) {
            console.log('MARMIC DEBUG: Error stringifying:', error)
            // Si no se puede convertir a JSON, usar método de keys como fallback
            const keys = Object.keys(services)
            console.log('MARMIC DEBUG: Object keys fallback:', keys)
            
            // Si solo tiene la propiedad __ob__ o no tiene propiedades, está vacío
            if (keys.length === 0 || (keys.length === 1 && keys[0] === '__ob__')) {
              console.log('MARMIC DEBUG: Empty - only __ob__ or no keys (fallback)')
              return true
            }
            
            // Si tiene otras propiedades además de __ob__, NO está vacío
            const nonObKeys = keys.filter(key => key !== '__ob__')
            console.log('MARMIC DEBUG: Non-observer keys (fallback):', nonObKeys)
            if (nonObKeys.length > 0) {
              console.log('MARMIC DEBUG: NOT empty - has real data (fallback)')
              return false
            }
          }
        }
        
        console.log('MARMIC DEBUG: NOT empty - default case')
        return false
      }
      
      // Determinar si la orden tiene servicios vacíos o no tiene servicios
      const hasEmptyServices = isEmptyMarmicServices(this.data_order?.marmic_services)
      
      console.log('MARMIC DEBUG: hasEmptyServices:', hasEmptyServices)
      console.log('MARMIC DEBUG: isDisabled:', this.isDisabled)
      
      // Solo cargar template si:
      // 1. La orden no tiene servicios de Marmic guardados
      // 2. Tiene subsidary/restaurant válido
      // 3. La orden NO está COMPLETED ni CANCELED
      const shouldLoadTemplate = hasEmptyServices && 
                                 this.data_order?.subsidary && 
                                 this.data_order.subsidary.id &&
                                 !this.isDisabled
      
      console.log('MARMIC DEBUG: shouldLoadTemplate:', shouldLoadTemplate)
      
      if (shouldLoadTemplate) {
        console.log('MARMIC DEBUG: Loading template for subsidary:', this.data_order.subsidary.id)
        this.loadMarmicTemplate(this.data_order.subsidary.id)
      } else {
        console.log('MARMIC DEBUG: NOT loading template because:')
        console.log('  - hasEmptyServices:', hasEmptyServices)
        console.log('  - has subsidary:', !!(this.data_order?.subsidary && this.data_order.subsidary.id))
        console.log('  - isDisabled:', this.isDisabled)
      }
      
      // Cargar datos existentes si hay
      if (this.data_order?.marmic_services) {
        console.log('MARMIC DEBUG: Loading existing marmic_services...')
        console.log('MARMIC DEBUG: Raw marmic_services:', this.data_order.marmic_services)
        
        let savedData = {}
        
        try {
          // Si es un string, parsearlo como JSON
          if (typeof this.data_order.marmic_services === 'string') {
            console.log('MARMIC DEBUG: Parsing string data')
            savedData = JSON.parse(this.data_order.marmic_services)
          } 
          // Si ya es un objeto, usarlo directamente (puede ser Observer)
          else if (typeof this.data_order.marmic_services === 'object') {
            console.log('MARMIC DEBUG: Using object data directly')
            // Para Observer de Vue, crear una copia simple del objeto
            savedData = JSON.parse(JSON.stringify(this.data_order.marmic_services))
          }
          
          console.log('MARMIC DEBUG: Processed savedData:', savedData)
        } catch (error) {
          console.warn('MARMIC DEBUG: Error parsing marmic_services:', error)
          savedData = {}
        }
        
        // Manejar tanto el formato nuevo (objeto con 'services') como el formato antiguo (array directo)
        let savedServices = []
        if (savedData.services && Array.isArray(savedData.services)) {
          savedServices = savedData.services
          this.saveForNextOrders = savedData.saveForNextOrders || false
        } else if (Array.isArray(savedData)) {
          savedServices = savedData
          // Buscar configuración de saveForNextOrders en el array (formato antiguo)
          const saveConfig = savedServices.find(s => s.hasOwnProperty && s.hasOwnProperty('saveForNextOrders'))
          if (saveConfig) {
            this.saveForNextOrders = saveConfig.saveForNextOrders || false
          }
        }
        
        if (Array.isArray(savedServices)) {
          // Cargar servicios regulares
          this.services.forEach(service => {
            const saved = savedServices.find(s => s.id === service.id)
            if (saved) {
              service.selected = true
              service.selectedPrice = saved.selectedPrice
            }
          })
          
          // Cargar servicio personalizado si existe
          const customSaved = savedServices.find(s => s.id === 'custom')
          if (customSaved) {
            this.customService.selected = true
            this.customService.name = customSaved.name
            this.customService.price = customSaved.price
          }
        }
      }
    },
    methods: {
      formatCurrency(value) {
        return value ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value) : '-'
      },
      onServiceSelect(service) {
        if (service.selected) {
          // Si se selecciona el servicio por primera vez, asignar precio por defecto
          if (!service.selectedPrice) {
            const defaultPrice = this.getDefaultPrice(service.name, service.id)
            if (defaultPrice) {
              service.selectedPrice = defaultPrice
              console.log(`Auto-assigned default price for ${service.name} (ID: ${service.id}): $${defaultPrice}`)
            }
          }
        } else {
          // Si se deselecciona, limpiar el precio
          service.selectedPrice = ''
        }
        this.calculateTotal()
      },
      onCustomServiceSelect() {
        if (!this.customService.selected) {
          this.customService.name = ''
          this.customService.price = ''
        }
        this.calculateTotal()
      },
      calculateTotal() {
        // El total se recalcula automáticamente mediante el computed property
      },
      clearAllServices() {
        // Deseleccionar todos los servicios y resetear precios
        this.services.forEach(service => {
          service.selected = false
          service.selectedPrice = ''
          service.hasError = false
        })
        
        // Limpiar servicio personalizado
        this.customService.selected = false
        this.customService.name = ''
        this.customService.price = ''
        this.customService.hasNameError = false
        this.customService.hasPriceError = false
        
        this.calculateTotal()
      },
      clearServiceError(service) {
        service.hasError = false
        this.calculateTotal()
      },
      clearCustomServiceNameError() {
        this.customService.hasNameError = false
      },
      clearCustomServicePriceError() {
        this.customService.hasPriceError = false
        this.calculateTotal()
      },
      getDefaultPrice(serviceName, serviceId) {
        // Mapeo específico por ID según los precios exactos solicitados
        const pricesByServiceId = {
          // Servicios de $325
          1: 325,   // Deli
          2: 325,   // Rotisserie 
          4: 325,   // Deli & Rotisserie Combo
          5: 325,   // Bakery
          15: 325,  // Combo (nuevo servicio agregado)
          
          // Servicios de $125
          3: 125,   // Rotisserie (Small) - segundo Rotisserie
          6: 125,   // Bakery (Small) - segundo Bakery
          7: 125,   // Bakery Extra - tercer Bakery
          8: 125,   // Pizza Oven
          9: 125,   // Grill
          10: 125,  // Fryer
          // 11: SIN PRECIO POR DEFECTO - Burner (deben seleccionar manualmente)
          // 12: SIN PRECIO POR DEFECTO - Prep Line (deben seleccionar manualmente)
          
          // Servicios de $250
          13: 250,  // Hinge Kit
          14: 250,  // Access Panel
        }
        
        // Si hay un precio específico para este ID, usarlo
        if (pricesByServiceId.hasOwnProperty(serviceId)) {
          const price = pricesByServiceId[serviceId]
          console.log(`✅ Auto-price: ${serviceName} (ID: ${serviceId}) → $${price}`)
          return price
        }
        
        // Servicios sin precio por defecto (deben seleccionar manualmente)
        const servicesWithoutDefaultPrice = [11, 12] // Burner, Prep Line
        if (servicesWithoutDefaultPrice.includes(serviceId)) {
          console.log(`⚪ No auto-price: ${serviceName} (ID: ${serviceId}) - Manual selection required`)
          return null
        }
        
        // Mapeo de fallback por nombre para casos no cubiertos
        const fallbackPrices = {
          'Deli': 325,
          'Rotisserie': 325,
          'Rotisserie (Small)': 125,
          'Deli & Rotisserie Combo': 325,
          'Bakery': 325,
          'Bakery (Small)': 125,
          'Bakery Extra': 125,
          'Pizza Oven': 125,
          'Fryer': 125,
          'Grill': 125,
          'Hinge Kit': 250,
          'Access Panel': 250,
          'Combo': 325
          // Burner y Prep Line intencionalmente omitidos para requerir selección manual
        }
        
        const fallbackPrice = fallbackPrices[serviceName]
        if (fallbackPrice) {
          console.log(`⚠️ Fallback price: ${serviceName} → $${fallbackPrice}`)
          return fallbackPrice
        }
        
        console.log(`❌ No price found for: ${serviceName} (ID: ${serviceId})`)
        return null
      },
      params() {
        const allServices = [...this.servicesWithPrices]
        
        console.log('MARMIC DEBUG: Building params...')
        console.log('MARMIC DEBUG: servicesWithPrices:', this.servicesWithPrices)
        console.log('MARMIC DEBUG: customService:', this.customService)
        console.log('MARMIC DEBUG: customService.selected:', this.customService.selected)
        console.log('MARMIC DEBUG: customService.name:', this.customService.name)
        console.log('MARMIC DEBUG: customService.price:', this.customService.price)
        
        // Agregar servicio personalizado si está activo
        if (this.customService.selected && this.customService.name && this.customService.price) {
          const customServiceData = {
            id: 'custom',
            name: this.customService.name,
            selectedPrice: this.customService.price,
            price: this.customService.price,
            selected: true
          }
          allServices.push(customServiceData)
          console.log('MARMIC DEBUG: Added custom service:', customServiceData)
        } else {
          console.log('MARMIC DEBUG: Custom service NOT added - missing data')
          console.log('MARMIC DEBUG: - selected:', this.customService.selected)
          console.log('MARMIC DEBUG: - name valid:', !!(this.customService.name))
          console.log('MARMIC DEBUG: - price valid:', !!(this.customService.price))
        }
        
        // Crear objeto de configuración separado
        const servicesData = {
          services: allServices,
          saveForNextOrders: this.saveForNextOrders
        }
        
        console.log('MARMIC DEBUG: Final servicesData:', servicesData)
        
        const params = {
          id: this.record.id,
          marmic_services: JSON.stringify(servicesData),
          units_price: this.totalPriceWithCustom
        }
        
        console.log('MARMIC DEBUG: Final params:', params)
        return JSON.stringify(params)
      },
      save_services() {
        this.state.sending = true
        this.errorMessage = null
        this.successMessage = null
        
        // Limpiar errores previos
        this.services.forEach(service => {
          service.hasError = false
        })
        this.customService.hasNameError = false
        this.customService.hasPriceError = false
        
        // Validación: verificar servicios seleccionados sin precio
        const servicesWithoutPrice = this.selectedServices.filter(service => 
          !service.selectedPrice || parseFloat(service.selectedPrice) <= 0
        )
        
        // Marcar servicios con errores
        servicesWithoutPrice.forEach(service => {
          service.hasError = true
        })
        
        // Validación: verificar servicio personalizado incompleto
        let customServiceIncomplete = false
        if (this.customService.selected) {
          if (!this.customService.name || !this.customService.name.trim()) {
            this.customService.hasNameError = true
            customServiceIncomplete = true
          }
          if (!this.customService.price || parseFloat(this.customService.price) <= 0) {
            this.customService.hasPriceError = true
            customServiceIncomplete = true
          }
        }
        
        // Si hay errores de validación, mostrar mensaje y detener
        if (servicesWithoutPrice.length > 0 || customServiceIncomplete) {
          this.state.sending = false
          let errorMessages = []
          
          if (servicesWithoutPrice.length > 0) {
            const serviceNames = servicesWithoutPrice.map(s => s.name).join(', ')
            errorMessages.push(`The following selected services require a valid price: ${serviceNames}`)
          }
          
          if (customServiceIncomplete) {
            errorMessages.push('Custom service requires both a name and a valid price greater than $0')
          }
          
          this.errorMessage = errorMessages.join('. ')
          return
        }
        
        // Si es una nueva orden (id === 'new'), emitir evento en lugar de guardar
        if (this.record.id === 'new') {
          // Preparar datos para emitir al componente padre
          const allServices = [...this.servicesWithPrices]
          
          // Agregar servicio personalizado si está activo
          if (this.customService.selected && this.customService.name && this.customService.price) {
            const customServiceData = {
              id: 'custom',
              name: this.customService.name,
              selectedPrice: this.customService.price,
              price: this.customService.price,
              selected: true
            }
            allServices.push(customServiceData)
          }
          
          // Crear objeto de configuración
          const servicesData = {
            services: allServices,
            saveForNextOrders: this.saveForNextOrders
          }
          
          // Emitir evento con los datos
          this.$emit('marmic-services-updated', JSON.stringify(servicesData))
          
          this.state.sending = false
          
          // Mostrar mensaje de éxito
          this.successMessage = `Marmic services configured successfully (${allServices.length} services selected). They will be saved when the order is created.`
          
          console.log('MARMIC DEBUG: Services saved for new order:', servicesData)
          
          return
        }
        
        // Para órdenes existentes, usar el método original
        BackendService.order_update_marmic_services(this.params())
          .then((response) => {
            if (response.isOk) {
              location.reload()
            } else {
              this.state.sending = false
              this.errorMessage = response.error?.message || 'Error saving services'
            }
          })
          .catch((error) => {
            this.state.sending = false
            this.errorMessage = error.message || 'Unexpected error processing request'
          })
      },
      loadMarmicTemplate(subsidaryId) {
        // Cargar template de servicios guardado previamente para esta subsidary
        console.log('MARMIC DEBUG: loadMarmicTemplate called with subsidaryId:', subsidaryId)
        BackendService.get_marmic_template(subsidaryId)
          .then((response) => {
            console.log('MARMIC DEBUG: Backend response:', response)
            if (response.isOk && response.config && response.config.length > 0) {
              console.log('MARMIC DEBUG: Applying template with', response.config.length, 'services')
              // Aplicar la configuración del template
              response.config.forEach(savedService => {
                console.log('MARMIC DEBUG: Processing service:', savedService)
                if (savedService.id === 'custom') {
                  this.customService.selected = true
                  this.customService.name = savedService.name
                  this.customService.price = savedService.price
                } else {
                  const service = this.services.find(s => s.id === savedService.id)
                  if (service) {
                    service.selected = true
                    service.selectedPrice = savedService.selectedPrice
                    console.log('MARMIC DEBUG: Applied service:', service.name, 'with price:', savedService.selectedPrice)
                  }
                }
              })
              
              // Si el template tenía saveForNextOrders=true, aplicarlo también
              if (response.saveForNextOrders === true) {
                console.log('MARMIC DEBUG: Setting saveForNextOrders to true')
                this.saveForNextOrders = true
              }
            } else {
              console.log('MARMIC DEBUG: No template found or empty config')
            }
          })
          .catch((error) => {
            console.log('MARMIC DEBUG: Error loading template:', error)
          })
      },
    }
  }
  </script>
  
  <style scoped>
  /* === MAIN LAYOUT STYLES === */
  .container {
    max-width: 100%;
  }

  /* === CARD STYLES === */
  .card {
    border: none;
    border-radius: 12px;
  }

  .card-header {
    border-radius: 12px 12px 0 0 !important;
    border-bottom: 2px solid rgba(0,0,0,0.1);
    padding: 1rem 1.5rem;
  }

  .card-body {
    padding: 1.5rem;
  }

  /* === SERVICE CARD STYLES === */
  .service-card {
    border: 2px solid #e9ecef;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: white;
    height: 100%;
    min-height: 80px;
  }

  .service-card:hover {
    border-color: #0d6efd;
    box-shadow: 0 2px 8px rgba(13, 110, 253, 0.1);
    transform: translateY(-2px);
  }

  .service-card.selected {
    border-color: #0d6efd;
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  }

  .service-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* === CUSTOM SERVICE CARD === */
  .custom-service-card {
    border: 2px dashed #28a745;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: white;
  }

  .custom-service-card.selected {
    border-style: solid;
    background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
  }

  .custom-fields {
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* === PRICE CONFIGURATION STYLES === */
  .service-price-item {
    transition: all 0.3s ease;
    background: white;
  }

  .service-price-item:hover {
    background: #f8f9fa;
    transform: translateX(5px);
  }

  .border-success {
    border-color: #28a745 !important;
    background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
  }

  /* === FORM STYLES === */
  .form-check-input:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }

  .form-floating > .form-control {
    border-radius: 6px;
  }

  .form-select {
    border-radius: 6px;
    transition: all 0.3s ease;
  }

  .form-select:focus,
  .form-control:focus {
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
  }

  /* === TABLE STYLES === */
  .table {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .table-dark {
    background: linear-gradient(135deg, #495057 0%, #343a40 100%);
  }

  .table-hover tbody tr:hover {
    background-color: rgba(13, 110, 253, 0.05);
    transform: scale(1.01);
    transition: all 0.2s ease;
  }

  /* === BADGE STYLES === */
  .badge {
    border-radius: 6px;
    font-weight: 500;
  }

  /* === BUTTON STYLES === */
  .btn {
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .btn-success {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border: none;
  }

  .btn-success:hover {
    background: linear-gradient(135deg, #218838 0%, #1e7e34 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }

  /* === ICON STYLES === */
  .fa {
    transition: all 0.3s ease;
  }

  .fa-2x {
    font-size: 2em;
  }

  /* === ANIMATION STYLES === */
  .spinner-border-sm {
    width: 1rem;
    height: 1rem;
  }

  /* === ALERT STYLES === */
  .alert {
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
  }

  /* === RESPONSIVE STYLES === */
  @media (max-width: 768px) {
    .card-body {
      padding: 1rem;
    }
    
    .service-card {
      min-height: 60px;
    }
    
    .btn-lg {
      padding: 0.5rem 1rem;
      font-size: 1rem;
    }
  }

  /* === STATUS INDICATORS === */
  .text-success {
    color: #28a745 !important;
  }

  .text-primary {
    color: #0d6efd !important;
  }

  .text-muted {
    color: #6c757d !important;
  }

  /* === SHADOW UTILITIES === */
  .shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .shadow {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  }

  /* === ERROR STATES === */
  .has-error {
    border-color: #dc3545 !important;
    background-color: #fff5f5 !important;
  }

  .is-invalid {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }

  .invalid-feedback {
    display: block !important;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: #dc3545;
  }

  .service-price-item.has-error {
    border-color: #dc3545 !important;
    background-color: #fff5f5;
  }
  </style>
  